import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { LoginPanel } from "../auth/LoginPanel";
import { useProcurement } from "@/context/ProcurementContext";
import { useAlerts } from "@/context/AlertContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, Bell } from "lucide-react";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [isLoginPanelOpen, setIsLoginPanelOpen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  
  // Safe context access with error handling
  let state, actions;
  try {
    const context = useProcurement();
    state = context.state;
    actions = context.actions;
  } catch (error) {
    console.error('Context error in AppLayout:', error);
    // Fallback values
    state = { currentUser: null };
    actions = { logout: () => {} };
  }

  // Safe alerts context access
  let alerts, resolveAlert;
  try {
    const alertsContext = useAlerts();
    alerts = alertsContext.getUnresolvedAlerts();
    resolveAlert = alertsContext.resolveAlert;
  } catch (error) {
    console.error('Alerts context error in AppLayout:', error);
    alerts = [];
    resolveAlert = () => {};
  }

  // Filter critical alerts for the banner
  const criticalAlerts = alerts.filter(alert => alert.severity === 'critical');

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col min-w-0">
          <AppHeader 
            onLoginClick={() => setIsLoginPanelOpen(true)}
            currentUser={state.currentUser}
            onLogout={actions.logout}
          />
          
          {/* Critical Alerts Banner */}
          {showAlerts && criticalAlerts.length > 0 && (
            <div className="bg-red-50 border-b border-red-200 px-4 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    {criticalAlerts.length} Critical Alert{criticalAlerts.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAlerts(false)}
                    className="text-red-600 hover:bg-red-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {criticalAlerts.slice(0, 3).map(alert => (
                  <div key={alert.id} className="flex items-center justify-between text-sm">
                    <span className="text-red-700">{alert.message}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => resolveAlert(alert.id)}
                      className="text-red-600 hover:bg-red-100 text-xs"
                    >
                      Resolve
                    </Button>
                  </div>
                ))}
                {criticalAlerts.length > 3 && (
                  <div className="text-xs text-red-600">
                    ... and {criticalAlerts.length - 3} more critical alerts
                  </div>
                )}
              </div>
            </div>
          )}
          
          <main className="flex-1 p-3 sm:p-4 md:p-6 bg-gradient-surface overflow-x-auto">
            <div className="max-w-full">
              {children}
            </div>
          </main>
        </div>

        <LoginPanel 
          isOpen={isLoginPanelOpen}
          onClose={() => setIsLoginPanelOpen(false)}
        />
      </div>
    </SidebarProvider>
  );
}